import React from 'react';
import { 
  ShieldCheck, 
  Lock, 
  CheckCircle2, 
  Trash2, 
  EyeOff, 
  Server, 
  FileText, 
  ExternalLink 
} from 'lucide-react';

export const PrivacyPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10 text-slate-300 text-sm leading-relaxed">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-semibold border border-emerald-800">
          <Lock className="w-3.5 h-3.5" />
          <span>Australian Privacy Principles (APP) Compliance</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
          Privacy Policy & Data Minimisation Framework
        </h1>
        <p className="text-slate-400 text-xs">
          Effective Date: 20 August 2026 • Compliant with Privacy Act 1988 (Cth)
        </p>
      </div>

      {/* Core Privacy Pillars Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-2 text-xs">
          <div className="w-8 h-8 rounded-xl bg-emerald-950 border border-emerald-800 text-emerald-400 flex items-center justify-center font-bold">
            <EyeOff className="w-4 h-4" />
          </div>
          <h3 className="font-bold text-white text-sm">Zero Passwords Collected</h3>
          <p className="text-slate-400">We never request or store your existing account passwords for breach checking or identity scoring.</p>
        </div>

        <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-2 text-xs">
          <div className="w-8 h-8 rounded-xl bg-teal-950 border border-teal-800 text-teal-400 flex items-center justify-center font-bold">
            <Server className="w-4 h-4" />
          </div>
          <h3 className="font-bold text-white text-sm">Australian Data Sovereignty</h3>
          <p className="text-slate-400">All databases and monitoring telemetry are hosted on secure Australian AWS data centres (ap-southeast-2).</p>
        </div>

        <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-2 text-xs">
          <div className="w-8 h-8 rounded-xl bg-blue-950 border border-blue-800 text-blue-400 flex items-center justify-center font-bold">
            <Trash2 className="w-4 h-4" />
          </div>
          <h3 className="font-bold text-white text-sm">Instant Account Deletion</h3>
          <p className="text-slate-400">Users can permanently purge all stored monitoring records and profile data from our servers in one click.</p>
        </div>
      </div>

      {/* Structured Sections */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8">
        {/* 1. What Information Is Collected */}
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-white">1. What Information We Collect</h2>
          <p>
            Under the Australian Privacy Principles, we collect only the minimum personal information necessary to deliver monitoring and breach alerts:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-xs text-slate-300">
            <li><strong>Account Details:</strong> Full name, email address, password hash (salted bcrypt), and optional mobile number.</li>
            <li><strong>Monitored Identifiers:</strong> Email addresses, phone numbers, or document reference numbers you explicitly choose to monitor.</li>
            <li><strong>Audit & Security Logs:</strong> IP address, browser user-agent, and login timestamps to detect unauthorized session access.</li>
          </ul>
        </div>

        {/* 2. Why It Is Collected */}
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-white">2. Why We Collect It (Purpose)</h2>
          <p>
            We process your information exclusively to:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-xs text-slate-300">
            <li>Match your identifiers against known corporate data breaches and dark web credential dumps.</li>
            <li>Dispatch real-time SMS and email threat alerts when new exposures are detected.</li>
            <li>Compute your personal Identity Security Score and offer tailored remediation roadmaps.</li>
          </ul>
        </div>

        {/* 3. Storage & Encryption */}
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-white">3. How Your Data Is Stored & Protected</h2>
          <p>
            We enforce state-of-the-art security controls designed in alignment with Australian Cyber Security Centre (ACSC) Essential Eight benchmarks:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-xs text-slate-300">
            <li><strong>Encryption in Transit:</strong> TLS 1.3 encryption on all connections with strict HSTS preloading.</li>
            <li><strong>Encryption at Rest:</strong> AES-256 database-level encryption with dedicated AWS Key Management Service (KMS).</li>
            <li><strong>Zero Plaintext Storage:</strong> Sensitive identifiers are stored as salted cryptographic hashes where possible.</li>
          </ul>
        </div>

        {/* 4. Data Sharing & Third Parties */}
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-white">4. Who We Share Data With</h2>
          <p className="font-semibold text-emerald-400">
            We do NOT sell, rent, or trade your personal data to marketing brokers, data aggregators, or unauthorized third parties.
          </p>
          <p className="text-xs">
            We only share data with essential technical service infrastructure providers (e.g. AWS Sydney for cloud hosting, Twilio for Australian SMS dispatch, Stripe for PCI-DSS payment tokenization) bound by strict Australian data confidentiality agreements.
          </p>
        </div>

        {/* 5. User Rights & Data Deletion */}
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-white">5. Your Right to Access & Delete Data</h2>
          <p>
            You have the complete right under Australian privacy law to inspect, correct, or permanently delete all personal information held about you. You can trigger automated account deletion in your Settings panel or by emailing <a href="mailto:privacy@idfraud.au" className="text-emerald-400 hover:underline font-mono">privacy@idfraud.au</a>.
          </p>
        </div>
      </div>
    </div>
  );
};
